import { MeetingOrderFilterType } from "../../../../shared/components/filters/orderTypeFilter/helpers"
import type { Organizer } from "../../../../shared/types/OrganizerTypes"
import type { MonetaryAmount } from "../../../currency/currencyTypes"
import type { DeliveryType } from "../../../merchant/merchant/production/meetingService/meetingServiceTypes"
import type { MeetingRowOrder } from "../../../nonAdminUser/reports/home/weekMenuTypes"
import type { Resource } from "../../../nonAdminUser/reports/upcomingOrders/resourceBookings/userResourceBookingTypes"
import { ResourceType } from "../../admin/organization/resources/resourcesTypes"
import { toMeetingAudience } from "../home/components/meetigs/helpers"
import type { MeetingAudience } from "../home/serviceCenterHomeTypes"

type UpcomingMeetingDetails = Readonly<{
    meetingName: string
    startDateTime: string
    endDateTime: string
    isExternal?: boolean
}>

type UpcomingResourceBookingOrder = Readonly<{
    id: number
    organizers: Organizer[]
    bookingDetails: Readonly<{ resources: Resource[] }>
    meetingDetails: UpcomingMeetingDetails & Readonly<{ location?: string }>
}>

type UpcomingMeetingServiceOrder = Readonly<{
    id: number
    uid: string
    orderType: string
    organizers: Organizer[]
    amount?: MonetaryAmount
    deliveryDetails?: Readonly<{
        time?: string
        type?: DeliveryType
        location?: Readonly<{ name: string }>
        isLocked?: boolean
        sittings?: number
    }>
    meetingDetails: UpcomingMeetingDetails & Readonly<{ location?: Readonly<{ name: string }> }>
}>

export type UpcomingMeetingOrder = UpcomingResourceBookingOrder | UpcomingMeetingServiceOrder

export type UpcomingMeetingsResponse = Readonly<{
    orders: UpcomingMeetingOrder[]
}>

export type UpcomingMeetingRow = MeetingRowOrder &
    Readonly<{
        orderKind: MeetingOrderFilterType.RESOURCE_BOOKING | MeetingOrderFilterType.MEETING_SERVICE
        resourceTypes: ReadonlyArray<ResourceType>
        audience?: MeetingAudience
    }>

export enum UpcomingMeetingsPeriod {
    TODAY = "TODAY",
    TOMORROW = "TOMORROW",
    THIS_WEEK = "THIS_WEEK",
    NEXT_7_DAYS = "NEXT_7_DAYS",
    NEXT_30_DAYS = "NEXT_30_DAYS",
    ALL = "ALL",
    CUSTOM = "CUSTOM",
}

export const UPCOMING_MEETINGS_PRESETS: ReadonlyArray<UpcomingMeetingsPeriod> = [
    UpcomingMeetingsPeriod.TODAY,
    UpcomingMeetingsPeriod.TOMORROW,
    UpcomingMeetingsPeriod.THIS_WEEK,
    UpcomingMeetingsPeriod.NEXT_7_DAYS,
    UpcomingMeetingsPeriod.NEXT_30_DAYS,
    UpcomingMeetingsPeriod.ALL,
]

export const ALL_RESOURCE_TYPES = "ALL"

export type ResourceTypeFilter = ResourceType | typeof ALL_RESOURCE_TYPES

export const RESOURCE_TYPE_FILTERS: ReadonlyArray<ResourceTypeFilter> = [
    ALL_RESOURCE_TYPES,
    ...Object.values(ResourceType),
]

// Only resource bookings carry bookingDetails; meeting service orders carry deliveries and an orderType.
function isResourceBookingOrder(order: UpcomingMeetingOrder): order is UpcomingResourceBookingOrder {
    return "bookingDetails" in order
}

function toResourceType(value: string): ResourceType | undefined {
    return Object.values(ResourceType).find((type) => type === value)
}

function toResourceTypes(resources: ReadonlyArray<Resource>): ResourceType[] {
    const types = resources
        .map((resource) => toResourceType(resource.resourceType))
        .filter((type): type is ResourceType => type !== undefined)
    return Array.from(new Set(types))
}

// The endpoint omits orderSummary, but MeetingRow needs it to tell a booking from a catering order.
export function toUpcomingMeetingRow(order: UpcomingMeetingOrder): UpcomingMeetingRow {
    const { id, organizers, meetingDetails } = order
    const { meetingName, startDateTime, endDateTime, isExternal } = meetingDetails
    const audience = toMeetingAudience(isExternal)

    if (isResourceBookingOrder(order)) {
        return {
            id,
            organizers,
            uid: "",
            orderKind: MeetingOrderFilterType.RESOURCE_BOOKING,
            resourceTypes: toResourceTypes(order.bookingDetails.resources ?? []),
            audience,
            orderSummary: { participants: 0, hasBooking: true, hasCatering: false, hasAudioVideoEquipment: false },
            meetingDetails: { meetingName, startDateTime, endDateTime, location: order.meetingDetails.location },
            deliveryDetails: {},
        }
    }

    return {
        id,
        organizers,
        uid: order.uid,
        orderKind: MeetingOrderFilterType.MEETING_SERVICE,
        resourceTypes: [],
        audience,
        orderSummary: { participants: 0, hasBooking: false, hasCatering: true, hasAudioVideoEquipment: false },
        meetingDetails: { meetingName, startDateTime, endDateTime, location: order.meetingDetails.location?.name },
        amount: order.amount,
        deliveryDetails: order.deliveryDetails ?? {},
    }
}
